import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const CARD_BORDER = "rgba(255,255,255,0.04)";

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{ fontFamily: "var(--display)", fontWeight: 600, fontSize: "clamp(18px, 2.5vw, 22px)", color: TEXT, margin: "32px 0 12px", letterSpacing: "-0.01em", lineHeight: 1.3 }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function A({ href, children }) {
  const isExternal = href.startsWith("http");
  return <a href={href} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function PullQuote({ children }) {
  return (
    <div style={{ borderLeft: `3px solid ${TEAL}`, paddingLeft: 20, margin: "32px 0" }}>
      <p style={{ fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.5, margin: 0 }}>{children}</p>
    </div>
  );
}
function InlineCTA() {
  return (
    <div style={{ margin: "40px 0", padding: "24px 28px", borderRadius: 14, background: TEAL_DIM, border: "1px solid rgba(78,205,196,0.25)" }}>
      <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT, margin: "0 0 12px", fontWeight: 600 }}>
        The first loop worth engineering is the one that only watches. The free AI Visibility Checker queries ChatGPT, Perplexity, and Google AI and reports your mention and citation status in under 60 seconds, no signup. That snapshot is the baseline your monitoring loop runs against.
      </p>
      <a href="/tools/ai-visibility-checker" style={{ color: TEAL, fontFamily: "var(--body)", fontSize: 15, fontWeight: 700, textDecoration: "none" }}>
        Check your AI visibility &#8594;
      </a>
    </div>
  );
}

const faqs = [
  {
    q: "Is loop engineering the same as loop marketing?",
    a: "No. Loop marketing is the strategy - the continuous Express, Tailor, Amplify, Evolve cycle that replaces the funnel. Loop engineering is the system that runs that cycle for you: the triggers, agents, and review gates that execute each rotation without a person re-starting it. Loop marketing is the what; loop engineering is the how.",
  },
  {
    q: "Do I need to know how to code to do loop engineering?",
    a: "No, for most of it. The triggers, monitoring, and review gates that make up a marketing loop are available as scheduled features in AEO and answer-engine tools, no code required. Connecting your own data sources directly can need engineering help, but most B2B teams engineer their first loops entirely from off-the-shelf tools.",
  },
  {
    q: "What is the first marketing loop I should engineer?",
    a: "Monitoring. Running your customer-intent prompts across ChatGPT, Perplexity, and Google AI on a schedule is read-only, so a mistake produces a wrong number, not a wrong page. It also gives you the baseline every later loop is measured against. Engineer the loop that only watches before the one that publishes.",
  },
  {
    q: "How is loop engineering different from regular marketing automation?",
    a: "Traditional marketing automation fires fixed rules: if a form is submitted, send email three. Loop engineering builds a system where AI agents interpret signals, draft work, and check each other, then feed the result back into the next cycle. It is the engineering discipline behind AI marketing automation, aimed specifically at running a growth loop.",
  },
  {
    q: "What is the maker-checker split and why does it matter?",
    a: "It means one agent drafts the work and a different agent reviews it - never the same one. An agent grading its own output tends to approve it. Separating the maker from the checker catches off-voice claims, missing sources, and errors before they reach the human review gate, which is what keeps an automated loop trustworthy.",
  },
  {
    q: "Where does the human stay involved in an engineered loop?",
    a: "At three points: deciding the strategy the loop runs, owning the brand voice the agents write in, and approving anything before it publishes. That final approval is the marketer in the loop principle. The agents handle volume and detection; the marketer handles judgment. A loop with no human gate is not engineered, it is unsupervised.",
  },
  {
    q: "Can a small B2B team do loop engineering?",
    a: "Often more easily than a large one. A two-person team cannot manually monitor 25 prompts across four AI platforms every week, audit 180 pages monthly, and track five competitors. An engineered loop makes that scope possible without adding headcount. The smaller the team, the more the leverage of a loop that runs itself matters.",
  },
];

const loopVsEngRows = [
  ["The question it answers", "What should our growth loop be?", "Who runs the loop, every cycle?"],
  ["Output", "A strategy: Express, Tailor, Amplify, Evolve", "A system: triggers, agents, review gates"],
  ["Discipline", "Marketing strategy", "AI automation and engineering"],
  ["The marketer's job", "Define the loop and its message", "Design the system, then approve the output"],
  ["Fails when", "The loop is never actually run", "The system ships work with no human gate"],
  ["Blaze CRM example", "Decides Amplify means winning AI citations", "Builds the weekly citation-monitoring loop"],
];

const loopPrimitives = [
  {
    n: "1",
    name: "Trigger",
    color: "#34d399",
    role: "The heartbeat",
    detail: "A schedule or a signal that starts each cycle. Without a trigger, a loop is just a checklist you still have to remember to run.",
    example: "Every Monday, or the moment a tracked prompt loses its citation.",
  },
  {
    n: "2",
    name: "Playbook",
    color: "#4FCDC4",
    role: "Reusable instructions",
    detail: "The standing context an agent reads every run, so you never re-brief the work. This is where your judgment lives once and gets applied every cycle.",
    example: "Your brand voice, your AEO checklist, your ideal-customer profile.",
  },
  {
    n: "3",
    name: "Maker agent",
    color: "#60a5fa",
    role: "Drafts the work",
    detail: "The agent that produces the output: the audit, the brief, the refreshed page. It reads the playbook and the trigger's input and does the cycle's actual work.",
    example: "Writes the FAQ section from the brand playbook and the prompt data.",
  },
  {
    n: "4",
    name: "Checker agent",
    color: "#f59e0b",
    role: "Reviews the maker",
    detail: "A separate agent that grades the maker's output against the playbook. Never the same agent - an agent reviewing its own work tends to approve it.",
    example: "Flags off-voice claims, missing sources, and broken internal links.",
  },
  {
    n: "5",
    name: "Review gate",
    color: "#a78bfa",
    role: "The human approval",
    detail: "The marketer who approves anything before it ships. This is the marketer in the loop. The loop runs itself up to this line and stops for a person.",
    example: "You approve the refreshed page, or send it back with a note.",
  },
];

const stageMap = [
  {
    stage: "Express",
    verb: "Define your perspective",
    engineer: "Encode the brand voice and point of view as a playbook the agents read every run.",
    human: "You write the perspective once; agents apply it.",
  },
  {
    stage: "Tailor",
    verb: "Personalize at scale",
    engineer: "An agent adapts the message to a segment, pulling from your CRM through a connector.",
    human: "You set the segments; the loop fills them.",
  },
  {
    stage: "Amplify",
    verb: "Meet buyers in AI answers",
    engineer: "Schedule the AEO work: audit pages, fix citation signals, publish where buyers ask AI.",
    human: "You pick the prompts that matter; the loop optimizes for them.",
  },
  {
    stage: "Evolve",
    verb: "Learn and improve",
    engineer: "Monitoring feeds results back into the next cycle, so the loop sharpens itself.",
    human: "You read the trend; the loop surfaces it.",
  },
];

const maturityTiers = [
  {
    tier: "Loop 1",
    name: "Monitoring",
    color: "#34d399",
    risk: "Read-only - nothing it produces gets published",
    detail: "Run your customer-intent prompts across the AI platforms on a schedule and track citation share over time. If it breaks, you get a wrong number, not a wrong page.",
    rule: "Engineer this first.",
  },
  {
    tier: "Loop 2",
    name: "Auditing",
    color: "#f59e0b",
    risk: "Produces a to-do list, not published output",
    detail: "A scheduled AEO audit that crawls on a cadence and flags only what changed. A bad audit wastes an afternoon chasing a false flag - it does not ship to customers.",
    rule: "Engineer once Loop 1 runs clean.",
  },
  {
    tier: "Loop 3",
    name: "Content generation",
    color: "#a78bfa",
    risk: "Produces output that can ship",
    detail: "Draft generation and page refresh behind a maker-checker split and a human gate. A bad draft that skips review becomes a published mistake.",
    rule: "Engineer last, never without a review gate.",
  },
];

function Content() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a },
    })),
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to engineer your first marketing loop",
    "description": "A maturity sequence for building a self-running AI-agent marketing loop, starting with the lowest-risk loop and ending with the one that publishes.",
    "step": [
      { "@type": "HowToStep", "name": "Pick one loop", "text": "Choose a single repeating marketing job to engineer first, not your whole strategy at once. Monitoring is the usual starting point." },
      { "@type": "HowToStep", "name": "Start with the loop that only watches", "text": "Engineer monitoring first. It is read-only, so a mistake produces a wrong number rather than a wrong page, and it gives every later loop a baseline." },
      { "@type": "HowToStep", "name": "Write the playbook", "text": "Encode the standing context the agents read every run: brand voice, AEO checklist, and the customer-intent prompts that matter." },
      { "@type": "HowToStep", "name": "Split the maker from the checker", "text": "Have one agent draft the work and a separate agent review it against the playbook. Never let the same agent grade its own output." },
      { "@type": "HowToStep", "name": "Add the human review gate", "text": "Require marketer approval before anything publishes. The loop runs itself up to this line and stops for a person." },
      { "@type": "HowToStep", "name": "Let it run, then advance a tier", "text": "Run the loop until the inputs stop surprising you, then engineer the next, riskier loop. Trust in the inputs is the signal to advance, not time on the calendar." },
    ],
  };

  return (
    <article style={{ maxWidth: 720, margin: "0 auto" }}>
      {/* FAQPage + HowTo JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />

      {/* Overview box */}
      <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: "20px 24px", marginBottom: 40 }} id="quick-answer">
        <P><Strong>Loop engineering</Strong> is the practice of building the AI-agent system that runs your marketing growth loop on its own, so you design the loop instead of turning the crank every cycle. The term comes from AI software engineering; this guide applies it to the marketing loop. For the short definition, see the <A href="/glossary/loop-engineering">loop engineering</A> glossary entry.</P>
        <ul style={{ margin: "16px 0 0 0", paddingLeft: 20 }}>
          <Li>Loop marketing is the strategy; loop engineering is the system that runs it.</Li>
          <Li>An engineered loop has five parts: a trigger, a playbook, a maker agent, a checker agent, and a human review gate.</Li>
          <Li>Engineer in order of risk: the loop that only watches goes first, the loop that publishes goes last.</Li>
          <Li>It is the operational layer of <A href="/glossary/ai-marketing-automation">AI marketing automation</A> - the Scale stage of the 5 A's.</Li>
        </ul>
      </div>

      {/* Section 1 */}
      <H2 id="what-is-loop-engineering">What is loop engineering?</H2>
      <P>Loop engineering is the discipline of building a system of AI agents that runs a repeating process on its own, so you design the process instead of executing it by hand each time. The term was popularized in AI software engineering by <A href="https://addyosmani.com/blog/loop-engineering/">engineer Addy Osmani</A>, who defines it as "replacing yourself as the person who prompts the agent" and designing the system that does it instead.</P>
      <P>For marketers, the repeating process is the growth loop. Loop marketing already replaced the linear funnel with a continuous cycle, but most teams still run that cycle by hand: a person kicks off each round of research, drafting, and analysis. Loop engineering asks the next question. If the loop repeats every week, who turns the crank? The answer should not be you. You build the system that turns it, then spend your time on the judgment the system cannot make.</P>
      <P>That distinction - the loop as a strategy versus the loop as a running system - is where most of the confusion lives, so it is worth pinning down before anything else.</P>

      {/* Section 2 */}
      <H2 id="loop-marketing-vs-loop-engineering">Loop marketing vs loop engineering: the strategy and the system</H2>
      <P>Loop marketing and loop engineering are not competing ideas. They are two halves of the same thing. <A href="https://blog.hubspot.com/marketing/loop-marketing-b2b">Loop marketing</A>, the framework HubSpot popularized, is the strategy: a self-reinforcing cycle of four stages - Express, Tailor, Amplify, Evolve - that compounds learning instead of ending at the sale. It tells you what your loop should be. It does not tell you who runs it.</P>
      <P>Loop engineering is the answer to that second question. It is the system that executes each rotation of the loop - the triggers that start a cycle, the agents that do the work, the gates that keep a human in control. One is a marketing decision; the other is an engineering one. A team can have a beautifully designed loop strategy and still hand-crank every cycle, which is the most common state today.</P>

      {/* Visual 1: strategy vs system table */}
      <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflow: "hidden" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 16, fontFamily: "var(--body)" }}>Loop marketing vs loop engineering</div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.15fr) minmax(0, 1.15fr)", gap: 0 }}>
          <div style={{ padding: "10px 10px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize: 9, color: TEXT_DIM, fontWeight: 600 }}>Dimension</div>
          </div>
          <div style={{ padding: "10px 10px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize: 9, color: TEXT_DIM, fontWeight: 700 }}>Loop marketing</div>
          </div>
          <div style={{ padding: "10px 10px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize: 9, color: TEAL, fontWeight: 700 }}>Loop engineering</div>
          </div>
          {loopVsEngRows.map(([label, lm, le], i) => (
            <div key={i} style={{ display: "contents" }}>
              <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 11, color: TEXT, fontWeight: 600 }}>{label}</div>
              <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 11, color: TEXT_DIM }}>{lm}</div>
              <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 11, color: TEXT_DIM }}>{le}</div>
            </div>
          ))}
        </div>
      </div>

      <PullQuote>Loop marketing tells you what the loop should be. Loop engineering decides who turns the crank, and the answer should not be you.</PullQuote>

      {/* Section 3 */}
      <H2 id="anatomy-of-an-engineered-loop">The anatomy of an engineered loop</H2>
      <P>An engineered loop has five parts. They are borrowed almost directly from how AI engineers build agent systems - automations, reusable skills, and separate sub-agents that check each other - and mapped onto marketing work. Get all five in place and the loop runs without you between review gates.</P>

      {/* Visual 2: the five parts */}
      <div style={{ margin: "24px 0 24px" }}>
        {loopPrimitives.map((p) => (
          <div key={p.n} style={{ display: "flex", gap: 14, padding: "16px 0", borderBottom: `1px solid ${CARD_BORDER}` }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: `1px solid ${p.color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "var(--mono)", fontSize: 13, fontWeight: 700, color: p.color }}>{p.n}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "var(--display)", fontSize: 15, fontWeight: 700, color: TEXT }}>{p.name}</span>
                <span style={{ fontFamily: "var(--body)", fontSize: 12, color: p.color, fontWeight: 600 }}>{p.role}</span>
              </div>
              <div style={{ fontFamily: "var(--body)", fontSize: 14, color: TEXT_DIM, lineHeight: 1.6 }}>{p.detail}</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: TEXT_DIM, marginTop: 6 }}>e.g. {p.example}</div>
            </div>
          </div>
        ))}
        <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: TEAL, marginTop: 14, fontWeight: 600 }}>&#8635; the review gate approves, the output ships, and the next trigger starts the loop again</div>
      </div>

      <P>The part marketers underrate is the split between the maker agent and the checker agent. It is tempting to let one agent draft and approve its own work, but an agent grading itself behaves like a writer proofreading at 2am - it sees what it meant to write, not what it wrote. A separate checker, reading against the same playbook, catches the off-voice sentence and the unsourced claim before they reach you. The two roles also map cleanly onto the broader <A href="/blog/what-is-ai-marketing-automation">AI marketing automation</A> principle that the riskiest work needs the most review.</P>
      <P>The five parts are not the whole picture. A real loop also reaches into your tools - the CRM, the content system, the analytics, the AI engines themselves - through connectors, often an <A href="/glossary/mcp-data-connector">MCP data connector</A> for teams that wire up their own data. Those connections are what let the loop act on live information rather than a stale export. But the five parts above are the skeleton. Connectors are the plumbing that feeds them.</P>

      {/* Section 4 */}
      <H2 id="engineering-the-loop-stage-by-stage">Engineering the loop, stage by stage</H2>
      <P>The four stages of loop marketing each become a piece of the system when you engineer them. The pattern is the same at every stage: you make the judgment once, encode it, and the loop applies it every cycle. The table below maps each stage to what you engineer and what stays your call.</P>

      {/* Visual 3: stage map */}
      <div style={{ margin: "24px 0 32px" }}>
        {stageMap.map((s) => (
          <div key={s.stage} style={{ padding: "14px 16px", marginBottom: 8, borderRadius: 10, background: "rgba(255,255,255,0.02)", border: `1px solid ${CARD_BORDER}`, borderLeft: `3px solid ${TEAL}` }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
              <span style={{ fontFamily: "var(--display)", fontSize: 15, fontWeight: 700, color: TEAL }}>{s.stage}</span>
              <span style={{ fontFamily: "var(--body)", fontSize: 12, color: TEXT_DIM }}>{s.verb}</span>
            </div>
            <div style={{ fontFamily: "var(--body)", fontSize: 13.5, color: TEXT_DIM, lineHeight: 1.6, marginBottom: 4 }}><span style={{ color: TEXT, fontWeight: 600 }}>Engineer: </span>{s.engineer}</div>
            <div style={{ fontFamily: "var(--body)", fontSize: 13.5, color: TEXT_DIM, lineHeight: 1.6 }}><span style={{ color: TEXT, fontWeight: 600 }}>You decide: </span>{s.human}</div>
          </div>
        ))}
      </div>

      <P>Amplify is where loop engineering earns its keep for a B2B team, because amplification now means showing up in AI answers, not just ads and posts. That is <A href="/glossary/answer-engine-optimization">answer engine optimization</A>, and it is the most automatable stage of the loop: auditing pages for citation signals, fixing structure, and tracking which prompts cite you are all scheduled, repeatable work. The strategy guides on <A href="/blog/how-to-improve-aeo-score">improving your AEO score</A> and <A href="/blog/how-to-improve-ai-citation-share">increasing AI citation share</A> describe the moves; loop engineering is what runs them every week instead of once.</P>
      <P>Evolve is the stage that makes it a loop rather than a line. The monitoring you engineer at the end feeds the next cycle's decisions - which prompts slipped, which pages decayed, which message landed - so each rotation starts smarter than the last. The methodology for that feedback lives in our guide on <A href="/blog/how-to-track-ai-citations">tracking AI citations</A>, and the work itself is the <A href="/platform/answer-engine-insights">Answer Engine Insights</A> module running on a schedule.</P>

      {/* Section 5 */}
      <H2 id="how-to-engineer-your-first-loop">How to engineer your first loop</H2>
      <P>Engineer one loop, not your whole strategy. And engineer them in order of risk: the loop that only watches goes first, the loop that publishes goes last. The sequence is not about difficulty. It is about what happens when a loop gets something wrong while running unattended.</P>

      {/* Visual 4: maturity ladder */}
      <div style={{ margin: "24px 0 32px" }}>
        {maturityTiers.map((t) => (
          <div key={t.tier} style={{ display: "flex", gap: 12, padding: "16px 0", borderBottom: `1px solid ${CARD_BORDER}` }}>
            <div style={{ width: 4, borderRadius: 2, background: t.color, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: t.color, fontWeight: 700 }}>{t.tier}</span>
                <span style={{ fontFamily: "var(--display)", fontSize: 15, fontWeight: 700, color: TEXT }}>{t.name}</span>
                <span style={{ fontFamily: "var(--body)", fontSize: 12, color: TEXT_DIM }}>{t.risk}</span>
              </div>
              <div style={{ fontFamily: "var(--body)", fontSize: 14, color: TEXT_DIM, lineHeight: 1.6 }}>{t.detail}</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: t.color, marginTop: 6, fontWeight: 600 }}>{t.rule}</div>
            </div>
          </div>
        ))}
      </div>

      <P>Picture Blaze CRM, a B2B software company with a two-person marketing team. They engineered their first loop in week one: a monitoring loop that runs 25 customer-intent prompts across the major AI platforms every Monday and tracks <A href="/glossary/citation-share">citation share</A> over time. It only reads, so the blast radius of a mistake is a wrong number on a dashboard. A month later, once they trusted the prompt set, they added a scheduled <A href="/blog/how-to-improve-aeo-score">AEO audit</A> loop. Only after a review workflow existed did they engineer the loop that drafts page refreshes - and that one runs behind a maker-checker split and a human gate.</P>
      <P>The signal to advance a tier is trust in the inputs, not time on the calendar. Blaze moved to the audit loop once a month of monitoring data stopped surprising them and the citation counts matched what they had spot-checked by hand. If the loop you already run is still surprising you, the next one is not ready to build. Each loop you add should ride on top of one you already trust.</P>

      <InlineCTA />

      {/* Section 6 */}
      <H2 id="what-loop-engineering-does-not-do">What loop engineering does not do</H2>
      <P>An engineered loop runs the work. It does not own the judgment. Three things stay with a person no matter how mature the system gets, and a loop that tries to absorb them produces fast, confident, off-strategy output.</P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Brand voice and point of view.</Strong> An agent can match a voice once it exists in the playbook. It cannot decide what the voice should be. A team that lets the loop define its perspective ends up sounding like every competitor running the same model.</Li>
        <Li><Strong>Strategy.</Strong> Which prompts to monitor, which segments matter, which competitors to track - the loop runs the list, but a person decides what is on it and revisits it as the market moves.</Li>
        <Li><Strong>The final review.</Strong> Every piece of agent-drafted work passes a human gate before publishing. This is the <A href="/glossary/marketer-in-the-loop">marketer in the loop</A> principle, and it is the line that separates an engineered loop from an unsupervised one.</Li>
      </ul>
      <P>This is not a limitation to engineer away over time. It is the design. The point of building the system is to hand it the volume so you get more time for exactly these three things, not less.</P>

      <PullQuote>A loop with no human gate is not engineered. It is unsupervised.</PullQuote>

      {/* Section 7 */}
      <H2 id="common-mistakes">Common loop-engineering mistakes</H2>
      <P>The failures of loop engineering are rarely technical. They are sequencing and governance mistakes - the same ones that sink any <A href="/glossary/ai-marketing-automation">AI marketing automation</A> effort, just aimed at a loop.</P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Engineering the publishing loop first.</Strong> Drafting is the exciting part, so teams automate it before they have any monitoring to tell them whether the output earns citations or sinks. Build the loop that watches before the loop that ships.</Li>
        <Li><Strong>No checker agent.</Strong> Letting the maker approve its own work. The split between drafting and reviewing is what makes an automated loop trustworthy; collapse it and you ship the model's first draft.</Li>
        <Li><Strong>No human gate.</Strong> The single most expensive mistake. One bad automated page on a customer-facing site costs more than the loop saved all quarter.</Li>
        <Li><Strong>Set and forget.</Strong> Loops degrade. A prompt set built six months ago is monitoring questions your buyers have stopped asking, and reporting clean while it does. Budget for tuning, not just for setup.</Li>
        <Li><Strong>Engineering a loop you run twice a year.</Strong> Automation earns back its setup cost through frequency. A tedious quarterly task is still a quarterly task; engineering it is motion, not leverage.</Li>
      </ul>
      <P>Every one of these is a process mistake wearing an engineering costume. Sequence the loops by risk, split the maker from the checker, gate every published output, and keep the playbooks current - do that, and the loop compounds. Skip them, and you have built a faster way to be wrong.</P>

      {/* FAQ section */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* Related reading */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/what-is-ai-marketing-automation">What Is AI Marketing Automation? The Complete Guide for 2026</A></Li>
        <Li><A href="/blog/5-as-ai-marketing-framework">The 5 A's of AI Marketing: A Complete Framework for B2B Marketers</A></Li>
        <Li><A href="/blog/how-to-track-ai-citations">How to Track AI Citations: A Weekly Monitoring Framework for B2B Marketers</A></Li>
        <Li><A href="/blog/how-to-improve-ai-citation-share">How to Increase Your AI Citation Share: A 7-Step Playbook</A></Li>
        <Li><A href="/blog/how-to-improve-aeo-score">How to Improve Your AEO Score: A Step-by-Step Guide for Each Category</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "what-is-loop-engineering",
  title: "What Is Loop Engineering? How B2B Marketers Run Growth Loops With AI",
  seoTitle: "What Is Loop Engineering? A Guide for B2B Marketers",
  excerpt:
    "Loop marketing is the strategy; loop engineering is the system that runs it. Here is how B2B marketers build an AI-agent loop that compounds on its own.",
  metaDescription:
    "A B2B marketer's guide to loop engineering: the parts of a self-running AI-agent marketing loop, how to build your first one, and where humans stay in control.",
  date: "2026-06-09",
  readTime: "11 min read",
  category: "AI Automation",
  tags: ["loop engineering", "loop marketing", "AI marketing automation", "AI Automation", "growth loops", "marketing AI agents"],
  Content,
};

export default post;
